import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Calendar, Clock, MapPin, Download, Share2, Star, Moon, Sun, Sparkles, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { KundliChart } from './KundliChart';
import { KundliDetails } from './KundliDetails';
import { KundliFAQ } from './KundliFAQ';
import { locationData } from './locationData';

interface BirthDetails {
  name: string;
  gender: 'male' | 'female';
  dateOfBirth: string;
  timeOfBirth: string;
  placeOfBirth: string;
  country: string;
  state: string;
  city: string;
  latitude: string;
  longitude: string;
  timezone: string;
}

interface KundliData {
  basicDetails: BirthDetails;
  planetaryPositions: any[];
  houses: any[];
  nakshatras: any;
  doshas: any;
  predictions: any;
}

interface KundliMakerProps {
  onClose?: () => void;
}

export function KundliMaker({ onClose }: KundliMakerProps = {}) {
  const [activeTab, setActiveTab] = useState<'form' | 'chart' | 'details'>('form');
  const [isGenerating, setIsGenerating] = useState(false);
  const [kundliData, setKundliData] = useState<KundliData | null>(null);
  const [birthDetails, setBirthDetails] = useState<BirthDetails>({
    name: '',
    gender: 'male',
    dateOfBirth: '',
    timeOfBirth: '',
    placeOfBirth: '',
    country: '',
    state: '',
    city: '',
    latitude: '',
    longitude: '',
    timezone: '+05:30'
  });

  // Get available states based on selected country
  const availableStates = birthDetails.country
    ? locationData.find(c => c.name === birthDetails.country)?.states || []
    : [];

  // Get available cities based on selected state
  const availableCities = birthDetails.state
    ? availableStates.find(s => s.name === birthDetails.state)?.cities || []
    : [];

  // Handle country selection
  const handleCountryChange = (countryName: string) => {
    const country = locationData.find(c => c.name === countryName);
    if (country) {
      setBirthDetails(prev => ({
        ...prev,
        country: countryName,
        state: '',
        city: '',
        timezone: country.timezone,
        latitude: '',
        longitude: '',
        placeOfBirth: ''
      }));
    }
  };

  // Handle state selection
  const handleStateChange = (stateName: string) => {
    setBirthDetails(prev => ({
      ...prev,
      state: stateName,
      city: '',
      latitude: '',
      longitude: '',
      placeOfBirth: ''
    }));
  };

  // Handle city selection
  const handleCityChange = (cityName: string) => {
    const state = availableStates.find(s => s.name === birthDetails.state);
    const city = state?.cities.find(c => c.name === cityName);
    if (city) {
      setBirthDetails(prev => ({
        ...prev,
        city: cityName,
        latitude: city.lat.toString(),
        longitude: city.lon.toString(),
        placeOfBirth: `${cityName}, ${birthDetails.state}, ${birthDetails.country}`
      }));
    }
  };

  const calculateVedicPositions = (date: Date, latitude: number, longitude: number) => {
    // Basic Vedic astrology calculations
    const julianDay = date.getTime() / 86400000 + 2440587.5;
    const T = (julianDay - 2451545.0) / 36525;

    // Calculate Ayanamsa (Lahiri)
    const ayanamsa = 23.85 + 0.013888889 * (julianDay - 2451545.0) / 365.25;

    const planets = [
      {
        name: 'Sun',
        position: ((280.46646 + 36000.76983 * T + 0.0003032 * T * T) % 360 - ayanamsa + 360) % 360,
        icon: '☉',
        color: 'text-orange-600'
      },
      {
        name: 'Moon',
        position: ((218.316 + 481267.881 * T) % 360 - ayanamsa + 360) % 360,
        icon: '☽',
        color: 'text-blue-400'
      },
      {
        name: 'Mars',
        position: ((355.433 + 19140.299 * T) % 360 - ayanamsa + 360) % 360,
        icon: '♂',
        color: 'text-red-600'
      },
      {
        name: 'Mercury',
        position: ((252.251 + 149472.674 * T) % 360 - ayanamsa + 360) % 360,
        icon: '☿',
        color: 'text-green-600'
      },
      {
        name: 'Jupiter',
        position: ((34.351 + 3034.906 * T) % 360 - ayanamsa + 360) % 360,
        icon: '♃',
        color: 'text-yellow-600'
      },
      {
        name: 'Venus',
        position: ((181.979 + 58517.816 * T) % 360 - ayanamsa + 360) % 360,
        icon: '♀',
        color: 'text-pink-600'
      },
      {
        name: 'Saturn',
        position: ((50.078 + 1222.114 * T) % 360 - ayanamsa + 360) % 360,
        icon: '♄',
        color: 'text-purple-600'
      }
    ];

    // Calculate houses (Placidus system adapted for Vedic)
    const LST = (100.46 + 0.985647 * julianDay + longitude + 15 * (date.getHours() + date.getMinutes() / 60)) % 360;
    const RAMC = LST;

    const houses = Array.from({ length: 12 }, (_, i) => {
      const houseNum = i + 1;
      const houseCusp = (RAMC + (i * 30) - ayanamsa + 360) % 360;
      return {
        house: houseNum,
        sign: Math.floor(houseCusp / 30),
        degree: houseCusp % 30,
        cusp: houseCusp
      };
    });

    return { planets, houses, ayanamsa };
  };

  const calculateNakshatra = (moonPosition: number) => {
    const nakshatras = [
      'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
      'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
      'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
      'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha',
      'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
    ];

    const lords = ['Ketu', 'Venus', 'Sun', 'Moon', 'Mars', 'Rahu', 'Jupiter', 'Saturn', 'Mercury'];
    
    const nakshatraIndex = Math.floor(moonPosition / 13.333333);
    const pada = Math.floor((moonPosition % 13.333333) / 3.333333) + 1;
    
    return {
      name: nakshatras[nakshatraIndex],
      lord: lords[nakshatraIndex % 9],
      pada: pada
    };
  };

  const checkDoshas = (planets: any[], houses: any[]) => {
    const doshas = {
      manglik: false,
      manglikIntensity: 'None',
      kaalSarpDosha: false,
      pitruDosha: false,
      details: [] as string[]
    };

    // Manglik Dosha check (Mars in houses 1, 2, 4, 7, 8, 12)
    const marsPosition = planets.find(p => p.name === 'Mars')?.position || 0;
    const marsHouse = houses.findIndex(h => {
      const nextHouse = houses[(houses.indexOf(h) + 1) % 12];
      return marsPosition >= h.cusp && marsPosition < nextHouse.cusp;
    }) + 1;

    if ([1, 2, 4, 7, 8, 12].includes(marsHouse)) {
      doshas.manglik = true;
      doshas.manglikIntensity = [1, 7, 8].includes(marsHouse) ? 'High' : 'Medium';
      doshas.details.push(`Mars in ${marsHouse}${marsHouse === 1 ? 'st' : marsHouse === 2 ? 'nd' : 'th'} house - Manglik Dosha present`);
    }

    return doshas;
  };

  const generateKundli = async () => {
    if (!birthDetails.name || !birthDetails.dateOfBirth || !birthDetails.timeOfBirth || !birthDetails.latitude) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsGenerating(true);

    try {
      // Parse date and time
      const [year, month, day] = birthDetails.dateOfBirth.split('-').map(Number);
      const [hours, minutes] = birthDetails.timeOfBirth.split(':').map(Number);
      const birthDate = new Date(year, month - 1, day, hours, minutes);
      
      const lat = parseFloat(birthDetails.latitude);
      const lon = parseFloat(birthDetails.longitude);

      // Calculate Vedic positions
      const { planets, houses, ayanamsa } = calculateVedicPositions(birthDate, lat, lon);

      // Calculate Nakshatra
      const moonPlanet = planets.find(p => p.name === 'Moon');
      const nakshatra = calculateNakshatra(moonPlanet?.position || 0);

      // Check Doshas
      const doshas = checkDoshas(planets, houses);

      // Generate basic predictions
      const sunSign = Math.floor(planets[0].position / 30);
      const moonSign = Math.floor((moonPlanet?.position || 0) / 30);
      const ascendant = Math.floor(houses[0].cusp / 30);

      const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
                     'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

      const kundli: KundliData = {
        basicDetails: birthDetails,
        planetaryPositions: planets.map(p => ({
          ...p,
          sign: signs[Math.floor(p.position / 30)],
          degree: Math.floor(p.position % 30),
          house: houses.findIndex(h => {
            const nextHouse = houses[(houses.indexOf(h) + 1) % 12];
            return p.position >= h.cusp && p.position < nextHouse.cusp;
          }) + 1
        })),
        houses: houses.map(h => ({
          ...h,
          sign: signs[h.sign],
          planets: planets.filter(p => {
            const nextHouse = houses[(houses.indexOf(h) + 1) % 12];
            return p.position >= h.cusp && p.position < nextHouse.cusp;
          }).map(p => p.name)
        })),
        nakshatras: nakshatra,
        doshas: doshas,
        predictions: {
          sunSign: signs[sunSign],
          moonSign: signs[moonSign],
          ascendant: signs[ascendant],
          ayanamsa: ayanamsa.toFixed(2)
        }
      };

      setKundliData(kundli);
      setActiveTab('chart');
      toast.success('Kundli generated successfully!');

    } catch (error: any) {
      console.error('Error generating Kundli:', error);
      toast.error('Failed to generate Kundli. Please check your inputs.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!kundliData) return;
    
    const content = JSON.stringify(kundliData, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kundli-${kundliData.basicDetails.name.replace(/\s+/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Kundli downloaded successfully!');
  };

  const handleShare = () => {
    if (!kundliData) return;
    
    const shareText = `Kundli for ${kundliData.basicDetails.name}\nSun Sign: ${kundliData.predictions.sunSign}\nMoon Sign: ${kundliData.predictions.moonSign}\nAscendant: ${kundliData.predictions.ascendant}\nNakshatra: ${kundliData.nakshatras.name}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'My Kundli',
        text: shareText
      }).then(() => {
        toast.success('Kundli shared successfully!');
      }).catch(() => {
        toast.error('Failed to share');
      });
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Kundli details copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-purple-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        {onClose && (
          <Button
            variant="ghost"
            onClick={onClose}
            className="mb-4 hover:bg-white/50"
          >
            <ArrowLeft className="size-4 mr-2" />
            Back
          </Button>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-rose-600 rounded-2xl shadow-lg">
              <Star className="size-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-rose-600 bg-clip-text text-transparent">
              Kundli Maker
            </h1>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Generate your detailed Vedic birth chart with planetary positions, houses, nakshatras, and dosha analysis
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 mb-8">
            <TabsTrigger value="form" className="flex items-center gap-2">
              <Calendar className="size-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="chart" disabled={!kundliData} className="flex items-center gap-2">
              <Sun className="size-4" />
              Chart
            </TabsTrigger>
            <TabsTrigger value="details" disabled={!kundliData} className="flex items-center gap-2">
              <Sparkles className="size-4" />
              Analysis
            </TabsTrigger>
          </TabsList>

          {/* Form Tab */}
          <TabsContent value="form">
            <Card className="p-6 md:p-8 bg-white/80 backdrop-blur-sm shadow-xl">
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="Enter full name"
                      value={birthDetails.name}
                      onChange={(e) => setBirthDetails(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-white"
                    />
                  </div>

                  {/* Gender */}
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender *</Label>
                    <Select
                      value={birthDetails.gender}
                      onValueChange={(value: 'male' | 'female') => setBirthDetails(prev => ({ ...prev, gender: value }))}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date of Birth */}
                  <div className="space-y-2">
                    <Label htmlFor="dob" className="flex items-center gap-2">
                      <Calendar className="size-4" />
                      Date of Birth *
                    </Label>
                    <Input
                      id="dob"
                      type="date"
                      value={birthDetails.dateOfBirth}
                      onChange={(e) => setBirthDetails(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                      className="bg-white"
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  {/* Time of Birth */}
                  <div className="space-y-2">
                    <Label htmlFor="tob" className="flex items-center gap-2">
                      <Clock className="size-4" />
                      Time of Birth *
                    </Label>
                    <Input
                      id="tob"
                      type="time"
                      value={birthDetails.timeOfBirth}
                      onChange={(e) => setBirthDetails(prev => ({ ...prev, timeOfBirth: e.target.value }))}
                      className="bg-white"
                    />
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Select onValueChange={handleCountryChange}>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select a country" />
                      </SelectTrigger>
                      <SelectContent>
                        {locationData.map(country => (
                          <SelectItem key={country.name} value={country.name}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* State */}
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Select onValueChange={handleStateChange}>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select a state" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableStates.map(state => (
                          <SelectItem key={state.name} value={state.name}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* City */}
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Select onValueChange={handleCityChange}>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select a city" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCities.map(city => (
                          <SelectItem key={city.name} value={city.name}>
                            {city.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Latitude */}
                  <div className="space-y-2">
                    <Label htmlFor="lat">Latitude *</Label>
                    <Input
                      id="lat"
                      type="number"
                      step="0.0001"
                      placeholder="e.g., 28.7041"
                      value={birthDetails.latitude}
                      onChange={(e) => setBirthDetails(prev => ({ ...prev, latitude: e.target.value }))}
                      className="bg-white"
                    />
                  </div>

                  {/* Longitude */}
                  <div className="space-y-2">
                    <Label htmlFor="lon">Longitude *</Label>
                    <Input
                      id="lon"
                      type="number"
                      step="0.0001"
                      placeholder="e.g., 77.1025"
                      value={birthDetails.longitude}
                      onChange={(e) => setBirthDetails(prev => ({ ...prev, longitude: e.target.value }))}
                      className="bg-white"
                    />
                  </div>

                  {/* Timezone */}
                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Select
                      value={birthDetails.timezone}
                      onValueChange={(value) => setBirthDetails(prev => ({ ...prev, timezone: value }))}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="+05:30">IST (UTC +5:30)</SelectItem>
                        <SelectItem value="+00:00">GMT (UTC +0:00)</SelectItem>
                        <SelectItem value="-05:00">EST (UTC -5:00)</SelectItem>
                        <SelectItem value="-08:00">PST (UTC -8:00)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-center pt-4">
                  <Button
                    onClick={generateKundli}
                    disabled={isGenerating}
                    className="bg-gradient-to-r from-orange-600 to-rose-600 hover:from-orange-700 hover:to-rose-700 text-white px-8 py-6 text-lg"
                  >
                    {isGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                        Generating Kundli...
                      </>
                    ) : (
                      <>
                        <Sparkles className="size-5 mr-2" />
                        Generate Kundli
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Chart Tab */}
          <TabsContent value="chart">
            {kundliData && (
              <div className="space-y-6">
                <Card className="p-6 bg-white/80 backdrop-blur-sm shadow-xl">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Birth Chart</h2>
                    <div className="flex gap-2">
                      <Button onClick={handleShare} variant="outline" size="sm">
                        <Share2 className="size-4 mr-2" />
                        Share
                      </Button>
                      <Button onClick={handleDownload} variant="outline" size="sm">
                        <Download className="size-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  </div>
                  <KundliChart data={kundliData} />
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Details Tab */}
          <TabsContent value="details">
            {kundliData && (
              <KundliDetails data={kundliData} />
            )}
          </TabsContent>
        </Tabs>

        {/* FAQ Section */}
        <div className="mt-12">
          <KundliFAQ />
        </div>
      </div>
    </div>
  );
}